import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../../components/TextInput/TextInput";
import Button from "../../components/Button/Button";
import LeftPanel from "../../components/LeftPanel/LeftPanel";
import OnboardingStepHeader from "../../components/OnboardingStepHeader/OnboardingStepHeader";
import SelectNew from "../../components/Select/SelectNew";
import AddressField, { AddressFieldValue } from "../../components/CustomAddress/CustomAddress";
import { saveStep1Data, goToNextStep } from "../../redux/slices/OfficeOnboardingSlice";
import { updateOfficeV2 } from "../../redux/slices/OfficeSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { logger } from "../../utils/logger";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { PayloadAction } from "@reduxjs/toolkit";
import { Office } from "../../models/office.model";
import { OfficeService } from "../../services/office.service";
import { formatPhoneNumber, validateAddress } from "../../utils";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Tooltip } from "@mui/material";

const log = logger.getLogger("OnboardingStep1");

interface FormData {
  officeName: string;
  officeAddress: string;
  officePhone: string;
  officeFax: string;
  province: string;
}

interface FormErrors {
  officeName: string;
  officeAddress: string;
  officePhone: string;
  officeFax: string;
  province: string;
}

const Step1: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { step1Data, isLoading } = useSelector((state: RootState) => state.officeOnboarding);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  // Get office name from current user's office (from signup)
  const officeFromSignup = currentUser?.offices?.[0];
  const officeId = officeFromSignup?.officeId;

  const [formData, setFormData] = useState<FormData>({
    officeName: step1Data?.officeName || officeFromSignup?.name || "",
    officeAddress: step1Data?.officeAddress || "",
    officePhone: step1Data?.officePhone || "",
    officeFax: step1Data?.officeFax || "",
    province: step1Data?.province || "ON",
  });

  const [officeAddress, setOfficeAddress] = useState<AddressFieldValue>({
    value: step1Data?.officeAddress || "",
    isValid: !!step1Data?.officeAddress,
    components: [],
  });

  // Store saved address fields for returning users
  const [savedAddressData, setSavedAddressData] = useState<{
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  } | null>(null);

  const [errors, setErrors] = useState<FormErrors>({
    officeName: "",
    officeAddress: "",
    officePhone: "",
    officeFax: "",
    province: "",
  });

  const [provinces, setProvinces] = useState<{ provinceId: string; name: string; provinceShortName: string; pst: number }[]>([]);
  const [isLoadingOffice, setIsLoadingOffice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch provinces from API
  useEffect(() => {
    const fetchProvinces = async () => {
      const provinceList = await OfficeService.getProvinces();
      setProvinces(provinceList);
    };
    fetchProvinces();
  }, []);

  // Fetch office data to prefill form (for returning users)
  useEffect(() => {
    const fetchOfficeData = async () => {
      if (!officeId) return;

      setIsLoadingOffice(true);
      try {
        const office = await OfficeService.getOfficeV2(officeId);

        if (office) {
          // Build formatted address string for display
          const addressParts = [office.street, office.street1, office.city, office.state, office.zip, office.country].filter(Boolean);
          const formattedAddress = addressParts.join(", ");

          // Only update form data if we don't have step1Data (first time loading)
          if (!step1Data) {
            setFormData({
              officeName: office.name || "",
              officeAddress: formattedAddress,
              officePhone: office.phone || "",
              officeFax: office.fax || "",
              province: "ON",
            });

            setOfficeAddress({
              value: formattedAddress,
              isValid: !!formattedAddress,
              components: [], // Components not available from saved data
            });
          }
          // Always save address fields for submission (needed even when step1Data exists)
          setSavedAddressData({
            street: office.street || "",
            street2: office.street2 || "",
            city: office.city || "",
            state: office.state || "",
            zip: office.zip || "",
            country: office.country || "",
          });
        }
      } catch (error) {
        log.error(`Failed to fetch office data: ${JSON.stringify(error)}`);
      } finally {
        setIsLoadingOffice(false);
      }
    };

    // Fetch office data if we have officeId and don't have savedAddressData
    // This ensures we always have address components for submission, even when returning from step 2
    if (officeId && !savedAddressData) {
      fetchOfficeData();
    }
  }, [officeId, savedAddressData, step1Data]);

  useEffect(() => {
    // Pre-fill from step1Data if available (user navigating between steps)
    if (step1Data) {
      setFormData({
        officeName: step1Data.officeName || officeFromSignup?.name || "",
        officeAddress: step1Data.officeAddress || "",
        officePhone: step1Data.officePhone || "",
        officeFax: step1Data.officeFax || "",
        province: "ON",
      });
      setOfficeAddress({
        value: step1Data.officeAddress || "",
        isValid: !!step1Data.officeAddress,
        components: [],
      });
    }
  }, [step1Data, officeFromSignup]);

  const validateField = (name: keyof FormData, value: string): string => {
    switch (name) {
      case "officeName":
        if (!value || !value.trim()) return "Office name is required.";
        const trimmedName = value.trim();
        if (trimmedName.length < 2) return "Invalid office name format.";
        if (trimmedName.length > 100) return "Invalid office name format.";
        // Allow alphanumeric, spaces, and special characters: ., -, &, ()
        const officeNameRegex = /^[a-zA-Z0-9\s.,&()-@]+$/;
        if (!officeNameRegex.test(trimmedName)) {
          return "Invalid office name format.";
        }
        return "";

      case "officeAddress":
        if (!value || !value.trim()) return "Office address is required.";
        const trimmedAddress = value.trim();
        if (trimmedAddress.length < 10) {
          return "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
        }
        if (trimmedAddress.length > 150) {
          return "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
        }
        // Allow alphanumeric, space, comma, hyphen, and period
        const addressRegex = /^[a-zA-Z0-9\s,.-]+$/;
        if (!addressRegex.test(trimmedAddress)) {
          return "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
        }
        return "";

      case "officePhone":
        if (!value || !value.trim()) return "Office phone number is required.";
        const phoneDigits = value.replace(/[^0-9]/g, "");
        if (phoneDigits.length !== 10) {
          return "Please enter a valid 10-digit phone number.";
        }
        // Check format: (XXX) XXX-XXXX or XXX-XXX-XXXX
        // Allow digits, parentheses, spaces, and hyphens
        const phoneFormatRegex = /^[\d\s()-]+$/;
        if (!phoneFormatRegex.test(value)) {
          return "Please enter a valid 10-digit phone number.";
        }
        return "";

      case "officeFax":
        // Fax is optional, only validate if value is provided
        if (value && value.trim()) {
          const faxDigits = value.replace(/[^0-9]/g, "");
          if (faxDigits.length !== 10) {
            return "Please enter a valid fax number.";
          }
          // Check format: (XXX) XXX-XXXX or XXX-XXX-XXXX
          // Allow digits, parentheses, spaces, and hyphens
          const faxFormatRegex = /^[\d\s()-]+$/;
          if (!faxFormatRegex.test(value)) {
            return "Please enter a valid fax number.";
          }
        }
        return "";

      case "province":
        if (!value || !value.trim()) return "Please select Territory.";
        // Check if it's a default/placeholder value (should not be empty or "Select province")
        if (value.toLowerCase() === "select Territory" || value === "") {
          return "Please select Territory";
        }
        return "";

      default:
        return "";
    }
  };

  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Format phone and fax numbers
    if (field === "officePhone" || field === "officeFax") {
      // Allow up to 14 characters for format: (XXX) XXX-XXXX
      if (value.length > 14) return;
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    // Prevent province from changing - always keep it as "ON"
    if (field === "province") {
      return; // Don't update province value
    }
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user makes a selection
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleInputBlur = (field: keyof FormData) => () => {
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      officeName: validateField("officeName", formData.officeName),
      officeAddress: "",
      officePhone: validateField("officePhone", formData.officePhone),
      officeFax: validateField("officeFax", formData.officeFax),
      province: validateField("province", formData.province),
    };

    // Validate office address - check both the string value and components
    if (!formData.officeAddress || !formData.officeAddress.trim()) {
      newErrors.officeAddress = "Office address is required.";
    } else {
      const trimmedAddress = formData.officeAddress.trim();
      if (trimmedAddress.length < 10 || trimmedAddress.length > 150) {
        newErrors.officeAddress = "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
      } else {
        // Check if we have address components (from Google Places)
        const hasAddressComponents = officeAddress.components && officeAddress.components.length > 0;
        if (hasAddressComponents) {
          const addressValidation = validateAddress(officeAddress.components);
          if (!addressValidation) {
            newErrors.officeAddress = "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
          }
        } else if (!savedAddressData) {
          // If no components and no saved data, validate the string format
          const addressRegex = /^[a-zA-Z0-9\s,.-]+$/;
          if (!addressRegex.test(trimmedAddress)) {
            newErrors.officeAddress = "Please enter a valid address in the format: Street, City, Province A1A 1A1.";
          }
        }
      }
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== "");
  };
  // Ensure province is always set to "ON"
  useEffect(() => {
    if (formData.province !== "ON") {
      setFormData(prev => ({ ...prev, province: "ON" }));
    }
  }, [formData.province]);

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    if (!officeId) {
      toast?.openToast("Office ID not found. Please try logging in again.", 2000, ToastVariants.Error);
      return;
    }

    // Check if we have new address components from Google Places
    const hasNewAddressComponents = officeAddress.components && officeAddress.components.length > 0;

    // Extract address components from Google Places response (if available)
    const addressValidation = hasNewAddressComponents ? validateAddress(officeAddress.components) : null;

    // If user entered new address but it's invalid, show error
    if (hasNewAddressComponents && !addressValidation) {
      setErrors(prev => ({ ...prev, officeAddress: "Please enter a valid address in the format: Street, City, Province A1A 1A1." }));
      toast?.openToast("Please enter a valid address with street, city, state, and postal code.", 2000, ToastVariants.Error);
      return;
    }

    // If no new address and no saved address data, show error
    if (!hasNewAddressComponents && !savedAddressData) {
      setErrors(prev => ({ ...prev, officeAddress: "Office address is required." }));
      toast?.openToast("Please enter an office address.", 2000, ToastVariants.Error);
      return;
    }
    setIsSubmitting(true);
    try {
      // Build the data object based on whether we have new address components or using saved data
      const data =
        hasNewAddressComponents && addressValidation
          ? {
              name: formData.officeName,
              street: `${addressValidation.streetNumber} ${addressValidation.street}`.trim(),
              street2: addressValidation.unit || "",
              city: addressValidation.city,
              state: addressValidation.state,
              zip: addressValidation.zip,
              // country: addressValidation.country,
              country: addressValidation.country?.trim() || "CA",
              phone: formData.officePhone,
              fax: formData.officeFax,
              province: formData.province,
            }
          : {
              // Returning user - use saved address data
              name: formData.officeName,
              street: savedAddressData!.street,
              street2: savedAddressData!.street2,
              city: savedAddressData!.city,
              state: savedAddressData!.state,
              zip: savedAddressData!.zip,
              // country: savedAddressData!.country,
              country: savedAddressData!.country?.trim() || "CA",
              phone: formData.officePhone,
              fax: formData.officeFax,
              province: formData.province,
            };

      // Call the v2 API to update office
      const result = (await dispatch(
        updateOfficeV2({
          officeId,
          data,
        })
      )) as PayloadAction<Office | undefined>;

      if (result.payload) {
        // Save step data and persist to localStorage (also advances to next step)
        await dispatch(
          saveStep1Data({
            data: {
              officeName: formData.officeName,
              officeAddress: formData.officeAddress,
              officePhone: formData.officePhone,
              officeFax: formData.officeFax,
              province: formData.province,
            },
            officeId: officeId,
          })
        );

        toast?.openToast("Office information saved successfully!", 2000, ToastVariants.Success);
        dispatch(goToNextStep());
      } else {
        toast?.openToast("Failed to save office information. Please try again.", 2000, ToastVariants.Error);
      }
    } catch (error) {
      log.error(`Failed to save step 1: ${JSON.stringify(error)}`);
      toast?.openToast("An error occurred. Please try again.", 2000, ToastVariants.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVideoPlay = () => {
    log.debug("Video play clicked");
  };

  const isFormValid =
    formData.officeName.trim() !== "" &&
    officeAddress.isValid &&
    formData.officePhone.trim() !== "" &&
    formData.province.trim() !== "" &&
    Object.values(errors).every(error => error === "");

  return (
    <div className="flex h-full min-h-0">
      {/* Left Panel */}
      <LeftPanel onVideoPlay={handleVideoPlay} />

      {/* Right Panel */}
      <div className="w-full md:w-2/3 bg-white flex flex-col h-full min-h-0">
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 z-10 bg-white flex-shrink-0">
          <OnboardingStepHeader currentStep={1} totalSteps={5} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex justify-center">
            <div className="p-8 w-full max-w-4xl">
              {/* Title and Description */}
              <div>
                <h1 className="text-xl leading-[30px] font-medium tracking-[-0.45px]">Office Information</h1>
                <p className="text-dn-slate mt-2 tracking-[-0.16px]">
                  Fill in your required office details. This will generate your letterhead which you can update anytime.
                </p>
              </div>

              {/* Form */}
              <div className="mt-8 space-y-3">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-sm text-docsigna-blue-dark font-medium">
                      Office Name
                      <span className="text-red-500">*</span>
                    </label>
                    <Tooltip title="(The office name is editable post sign up from Office Info tab)" placement="right" arrow>
                      <HelpOutlineIcon className="!text-base text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                  <input
                    type="text"
                    placeholder="Enter Office Name"
                    value={formData.officeName}
                    onChange={handleInputChange("officeName")}
                    onBlur={handleInputBlur("officeName")}
                    name="officeName"
                    readOnly
                    className="block w-full text-docsigna-blue-dark font-medium leading-none rounded-md shadow-none border border-docsigna-blue-dark p-2.5 bg-gray-100 cursor-not-allowed"
                  />
                  {errors.officeName && <p className="text-sm text-red-500 mt-1">{errors.officeName}</p>}
                </div>

                <div>
                  <AddressField
                    label="Office Address"
                    required={true}
                    initialValue={officeAddress.value}
                    initialComponents={officeAddress.components}
                    initialData={
                      savedAddressData
                        ? {
                            street: savedAddressData.street,
                            street1: savedAddressData.street2,
                            street2: "",
                            city: savedAddressData.city,
                            state: savedAddressData.state,
                            zip: savedAddressData.zip,
                            country: savedAddressData.country,
                          }
                        : undefined
                    }
                    error={!!errors.officeAddress}
                    errorMsg={errors.officeAddress || "Please enter a valid address"}
                    onChange={(val: AddressFieldValue) => {
                      setOfficeAddress(val);
                      setFormData(prev => ({ ...prev, officeAddress: val.value }));
                      // Clear error when address is selected
                      if (errors.officeAddress) {
                        setErrors(prev => ({ ...prev, officeAddress: "" }));
                      }
                    }}
                    hideGooglePlaceSearch={false}
                    addressType="office"
                    darkBg={false}
                    territoryValue={formData.province}
                    resetOnToggle={true}
                  />
                </div>
                <div>
                  <SelectNew
                    title="Province/Territory"
                    required={true}
                    value={formData.province}
                    placeHolder="Select Province/Territory"
                    onSelect={handleSelectChange("province")}
                    options={provinces.map(item => ({
                      label: item.name,
                      value: item.provinceShortName,
                      disabled: item.provinceShortName !== "ON", // Add this line
                    }))}
                    errorMsg={errors.province}
                  />
                </div>

                {/* Phone and Fax in one row */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <TextInput
                      label="Office Phone Number"
                      placeholder="Enter Office Phone Number"
                      value={formData.officePhone}
                      onChangeFunc={handleInputChange("officePhone")}
                      onBlur={handleInputBlur("officePhone")}
                      isRequired={true}
                      isError={!!errors.officePhone}
                      errorMsg={errors.officePhone}
                      name="officePhone"
                    />
                  </div>

                  <div className="flex-1">
                    <TextInput
                      label="Office Fax Number"
                      placeholder="Enter Office Fax Number"
                      value={formData.officeFax}
                      onChangeFunc={handleInputChange("officeFax")}
                      onBlur={handleInputBlur("officeFax")}
                      isRequired={false}
                      isError={!!errors.officeFax}
                      errorMsg={errors.officeFax}
                      name="officeFax"
                    />
                  </div>
                </div>

                {/* <div>
                  <SelectNew
                    title="Province/State"
                    required={true}
                    value={formData.province}
                    placeHolder="Select province"
                    onSelect={handleSelectChange("province")}
                    options={provinces.map(item => ({
                      label: item.name,
                      value: item.provinceShortName,
                    }))}
                    errorMsg={errors.province}
                  />
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer with Submit Button */}
        <div className="sticky bottom-0 z-10 bg-white flex-shrink-0 p-8">
          <div className="flex justify-end">
            <Button
              text="Save & Continue"
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading || isLoadingOffice || isSubmitting}
              variant={isLoading || isLoadingOffice || isSubmitting ? "loading" : "Primary"}
              width="fit"
              className="px-8 py-3"
              buttonStyle="square"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1;
